import React, { useMemo, memo, useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { Checkbox, Tooltip, Menu, Button, Space } from 'antd'
import { useTranslation } from 'react-i18next'
import { useVT } from 'virtualizedtableforantd4'
import {
	HEADER_HEIGHT,
	CHANGED_REQUIREMENT_STATUS,
	REQUIREMENT_PRIORITY
} from 'modules/core/constants'

import { ROUTES } from 'modules/navigation'
import ChangedStatusBadge from './ChangedStatusBadge'
import CombinedChangedStatusBadge from './CombinedChangedStatusBadge'

import { AkaButton, AkaTypography } from 'components'
import {
	StyledContractRegionGroupMatrix,
	ChapterCell,
	RequirementCell,
	CenterCell,
	ButtonRightWrapper,
	FilterMenuButtonWrapper,
	FilterMenuHeader,
	CustomFilterIcon,
	PriorityTag,
	RequirementLink
} from './styles'

const TableScrollY = `calc(100vh - (48px + ${HEADER_HEIGHT}px + 48px + 170px))`

const PRIORITY_VALUES_BY_CODE = {
	1: REQUIREMENT_PRIORITY.REQUIRED,
	2: REQUIREMENT_PRIORITY.OPTIONAL,
	3: REQUIREMENT_PRIORITY.CONDITIONAL
}

const PRIORITY_TAGS = {
	[REQUIREMENT_PRIORITY.REQUIRED]: { tagColor: 'red', tagLabel: 'P' },
	[REQUIREMENT_PRIORITY.OPTIONAL]: { tagColor: 'green', tagLabel: 'O' },
	[REQUIREMENT_PRIORITY.CONDITIONAL]: { tagColor: 'orange', tagLabel: 'K' }
}

const isFilterEqual = (s1, s2) => {
	if (s1 === s2) return true
	if (!s1 || !s2) return false
	if (s1.length !== s2.length) return false
	for (let i = 0; i < s1.length; i++) if (s1[i] != s2[i]) return false
	return true
}

const getRequirementStatuses = (requirement, changes) =>
	[
		changes?.newRequirementIds?.includes(requirement.id) &&
			CHANGED_REQUIREMENT_STATUS.NEW,
		changes?.changedRequirementIds?.includes(requirement.id) &&
			CHANGED_REQUIREMENT_STATUS.CHANGED,
		changes?.changedAssignmentRequirementIds?.includes(requirement.id) &&
			CHANGED_REQUIREMENT_STATUS.CHANGED_ASSIGNMENT
	].filter(Boolean)

const getRequirementRows = (chapter, changes, filters) => {
	const list = chapter.matrixRequirements

	const filtered =
		filters.changeStatus || filters.priority
			? list.filter(requirement => {
					const statuses = getRequirementStatuses(requirement, changes)

					const statusMatch = filters.changeStatus
						? statuses.some(s => filters.changeStatus.includes(s))
						: true

					const priorityMatch = filters.priority
						? filters.priority.includes(
								PRIORITY_VALUES_BY_CODE[requirement.priority]
							)
						: true

					return statusMatch && priorityMatch
				})
			: list

	return filtered.length
		? filtered.map(requirement => ({
				key: String(requirement.caliberId),
				requirement: {
					requirementId: requirement.id,
					caliberId: requirement.caliberId,
					prefixedCaliberId: requirement.prefixedCaliberId,
					title: requirement.title,
					chapterId: chapter.id,
					priority: PRIORITY_VALUES_BY_CODE[requirement.priority],
					changeStatus: getRequirementStatuses(requirement, changes)
				}
			}))
		: null
}

const getChapterRows = (chapter, changes, filters) => {
	const childChapters = chapter.chapters
		.map(childChapter => getChapterRows(childChapter, changes, filters))
		.filter(Boolean)

	const reqs = getRequirementRows(chapter, changes, filters)

	if (childChapters.length || reqs) {
		return {
			key: `chapter-id:${chapter.id}`,
			chapter: chapter.fullTitle,
			children: [...(reqs || []), ...childChapters]
		}
	}

	return null
}

const createTableData = (chapters, changes, filters) => {
	return chapters
		.map(chapter => getChapterRows(chapter, changes, filters))
		.filter(Boolean)
}

const NonContractRequirementMatrix = ({
	preselectedRequirementIds,
	chapters,
	onSubmit,
	isSubmitting,
	isSubmitted,
	changes,
	releaseId,
	isReadOnly
}) => {
	const [vt] = useVT(() => ({ scroll: { y: TableScrollY } }), [])
	const [t] = useTranslation()

	// --- PREFILL FIX ---
	// Normalize incoming preselected ids to strings so they match table keys
	const normalizedPreselected = preselectedRequirementIds.map(id => String(id))

	const [selectedRowKeys, setSelectedRowKeys] = useState(normalizedPreselected)

	const [selectedCheckboxes, setSelectedCheckboxes] = useState(
		Object.fromEntries(normalizedPreselected.map(id => [id, true]))
	)

	// --- when preselected changes → sync it into selection ---
	useEffect(() => {
		const normalized = preselectedRequirementIds.map(id => String(id))
		setSelectedRowKeys(normalized)
		setSelectedCheckboxes(Object.fromEntries(normalized.map(id => [id, true])))
	}, [preselectedRequirementIds])

	const [filters, setFilters] = useState({ changeStatus: null, priority: null })
	const [validationError, setValidationError] = useState(false)
	useEffect(() => {
		if (!isReadOnly) {
			setValidationError(selectedRowKeys.length === 0)
		}
	}, [selectedRowKeys, isReadOnly])
	const columns = [
		{
			title: t('approval.report.requirementsTable.selectAllRequirements'),
			width: 1000,
			fixed: 'left',
			render: (_, record) => {
				if (record.chapter) return <ChapterCell>{record.chapter}</ChapterCell>

				const r = record.requirement

				const linkParams = {
					pathname: `${ROUTES.catalog}/${releaseId}`,
					search: `?${new URLSearchParams({
						chapterId: r.chapterId,
						requirementId: r.prefixedCaliberId
					})}`
				}

				return (
					<RequirementCell>
						<RequirementLink to={linkParams} target="_blank">
							<div className="requirementId">{r.prefixedCaliberId}</div>
						</RequirementLink>
						<RequirementLink to={linkParams} target="_blank">
							<div>{r.title}</div>
						</RequirementLink>
					</RequirementCell>
				)
			}
		},
		{
			title: t('approval.report.requirementsTable.priority.title'),
			dataIndex: 'priority',
			fixed: 'left',
			width: 50,
			filterIcon: () => (
				<Tooltip title={t('approval.report.requirementsTable.priority.filter')}>
					<CustomFilterIcon />
				</Tooltip>
			),
			filters: [
				{
					text: t('approval.report.requirementsTable.priority.required'),
					value: REQUIREMENT_PRIORITY.REQUIRED
				},
				{
					text: t('approval.report.requirementsTable.priority.optional'),
					value: REQUIREMENT_PRIORITY.OPTIONAL
				},
				{
					text: t('approval.report.requirementsTable.priority.conditional'),
					value: REQUIREMENT_PRIORITY.CONDITIONAL
				}
			],
			filterDropdown: ({
				selectedKeys,
				setSelectedKeys,
				filters,
				clearFilters,
				confirm
			}) => (
				<>
					<FilterMenuHeader>
						<AkaTypography variant="body" color="white">
							{t('approval.report.requirementsTable.priority.title')}
						</AkaTypography>
					</FilterMenuHeader>

					<Menu
						multiple
						selectedKeys={selectedKeys}
						onSelect={({ selectedKeys: keys }) => setSelectedKeys(keys)}
						onDeselect={({ selectedKeys: keys }) => setSelectedKeys(keys)}
						items={filters.map(f => {
							const { tagColor, tagLabel } = PRIORITY_TAGS[f.value]
							return {
								key: f.value,
								label: (
									<Space>
										<Checkbox checked={selectedKeys.includes(f.value)} />
										<PriorityTag color={tagColor}>{tagLabel}</PriorityTag>
										{f.text}
									</Space>
								)
							}
						})}
					/>

					<FilterMenuButtonWrapper>
						<Button
							disabled={!selectedKeys.length}
							type="link"
							size="small"
							onClick={clearFilters}
						>
							{t('approval.report.requirementsTable.changedStatus.reset')}
						</Button>
						<Button size="small" onClick={() => confirm()} type="primary">
							{t('approval.report.requirementsTable.changedStatus.ok')}
						</Button>
					</FilterMenuButtonWrapper>
				</>
			),
			render: (_, record) => {
				if (!record.requirement) return null

				const { priority } = record.requirement
				const { tagColor, tagLabel } = PRIORITY_TAGS[priority]

				return (
					<CenterCell>
						<Tooltip
							placement="left"
							title={t(
								`approval.report.requirementsTable.priority.${priority}`
							)}
						>
							<PriorityTag color={tagColor}>{tagLabel}</PriorityTag>
						</Tooltip>
					</CenterCell>
				)
			}
		},
		{
			title: t('approval.report.requirementsTable.changedStatus.title'),
			width: 50,
			dataIndex: 'changeStatus',
			fixed: 'left',
			filterIcon: () => (
				<Tooltip
					title={t('approval.report.requirementsTable.changedStatus.filter')}
				>
					<CustomFilterIcon />
				</Tooltip>
			),
			filters: [
				{
					text: t('approval.report.requirementsTable.changedStatus.new'),
					value: CHANGED_REQUIREMENT_STATUS.NEW
				},
				{
					text: t('approval.report.requirementsTable.changedStatus.changed'),
					value: CHANGED_REQUIREMENT_STATUS.CHANGED
				}
			],
			filterDropdown: ({
				selectedKeys,
				setSelectedKeys,
				filters,
				clearFilters,
				confirm
			}) => (
				<>
					<FilterMenuHeader>
						<AkaTypography variant="body" color="white">
							{t('approval.report.requirementsTable.changedStatus.title')}
						</AkaTypography>
					</FilterMenuHeader>

					<Menu
						multiple
						selectedKeys={selectedKeys}
						onSelect={({ selectedKeys: keys }) => setSelectedKeys(keys)}
						onDeselect={({ selectedKeys: keys }) => setSelectedKeys(keys)}
						items={filters.map(f => ({
							key: f.value,
							label: (
								<Space>
									<Checkbox checked={selectedKeys.includes(f.value)} />
									<ChangedStatusBadge showTooltip={false} status={f.value} />
									{f.text}
								</Space>
							)
						}))}
					/>

					<FilterMenuButtonWrapper>
						<Button
							disabled={!selectedKeys.length}
							type="link"
							size="small"
							onClick={clearFilters}
						>
							{t('approval.report.requirementsTable.changedStatus.reset')}
						</Button>
						<Button size="small" onClick={() => confirm()} type="primary">
							{t('approval.report.requirementsTable.changedStatus.ok')}
						</Button>
					</FilterMenuButtonWrapper>
				</>
			),
			render: (_, record) => {
				if (!record.requirement) return null

				const statuses = record.requirement.changeStatus
				if (!statuses) return null

				if (
					statuses.length === 2 &&
					statuses.includes(CHANGED_REQUIREMENT_STATUS.CHANGED) &&
					statuses.includes(CHANGED_REQUIREMENT_STATUS.CHANGED_ASSIGNMENT)
				) {
					return (
						<CenterCell>
							<CombinedChangedStatusBadge statuses={statuses} />
						</CenterCell>
					)
				}

				return (
					<CenterCell>
						{statuses.map(s => (
							<ChangedStatusBadge key={s} status={s} />
						))}
					</CenterCell>
				)
			}
		}
	]

	const tableData = useMemo(() => {
		return createTableData(chapters, changes, filters)
	}, [chapters, changes, filters])

	// Build mapping from caliberId to requirementId for submission
	const caliberIdToRequirementId = useMemo(() => {
		const map = {}
		const buildMap = items =>
			(items || []).forEach(item => {
				if (item.requirement) {
					map[String(item.key)] = item.requirement.requirementId
				}
				if (item.children) buildMap(item.children)
			})
		buildMap(tableData)
		return map
	}, [tableData])

	// Ensure preselected keys are present in the current table data (flattened),
	// otherwise remove them so selection matches visible rows. This keeps
	// prefill working after filters/load.
	const prevPreselectedRef = useRef()
	const initialSyncDoneRef = useRef(false)

	useEffect(() => {
		const normalized = preselectedRequirementIds.map(id => String(id))

		const flattenKeys = items => {
			const ids = []
			;(items || []).forEach(item => {
				if (item.requirement) ids.push(String(item.key))
				if (item.children) ids.push(...flattenKeys(item.children))
			})
			return ids
		}

		const available = new Set(flattenKeys(tableData))

		// Guard: Don't filter if tableData is empty - data hasn't loaded yet
		if (available.size === 0) {
			return
		}

		const prevNormalized = prevPreselectedRef.current

		// If the prop `preselectedRequirementIds` changed, override selection
		if (!prevNormalized || !isFilterEqual(prevNormalized, normalized)) {
			// Trust the preselected IDs from props - don't filter against available
			// The backend ensures these are valid requirement IDs
			setSelectedRowKeys(normalized)
			setSelectedCheckboxes(
				Object.fromEntries(normalized.map(id => [id, true]))
			)
			initialSyncDoneRef.current = true
		} else if (initialSyncDoneRef.current) {
			// Only filter against available AFTER initial sync is done
			// This handles user filtering (removing unavailable keys)
			const validKeys = selectedRowKeys.filter(id => available.has(id))
			if (!isFilterEqual(selectedRowKeys, validKeys)) {
				setSelectedRowKeys(validKeys)
				const newMap = {}
				validKeys.forEach(id => (newMap[id] = true))
				setSelectedCheckboxes(newMap)
			}
		}

		prevPreselectedRef.current = normalized
	}, [tableData, preselectedRequirementIds, selectedRowKeys])

	// --- FIXED row selection ---
	const rowSelection = {
		selectedRowKeys,
		checkStrictly: false,

		getCheckboxProps: () => ({ disabled: isReadOnly }),

		onChange: newKeys => {
			const reqKeys = newKeys.filter(k => !k.startsWith('chapter-id:'))

			setSelectedRowKeys(reqKeys)

			const newMap = {}
			reqKeys.forEach(id => (newMap[id] = true))
			setSelectedCheckboxes(newMap)
		}
	}
	const handleSubmit = () => {
		// Convert caliberId keys to requirementIds for backend submission
		const caliberIds = Object.keys(selectedCheckboxes)
		const requirementIds = caliberIds
			.map(caliberId => caliberIdToRequirementId[caliberId])
			.filter(Boolean)
		onSubmit(requirementIds)
	}

	return (
		<>
			<StyledContractRegionGroupMatrix
				columns={columns}
				rowSelection={rowSelection}
				dataSource={tableData}
				pagination={false}
				components={vt}
				size="small"
				scroll={{ y: TableScrollY }}
				expandable={{ defaultExpandAllRows: true, indentSize: 3 }}
				onChange={(_, newFilters) => {
					if (!isFilterEqual(newFilters.changeStatus, filters.changeStatus)) {
						setFilters(f => ({ ...f, changeStatus: newFilters.changeStatus }))
					}
					if (!isFilterEqual(newFilters.priority, filters.priority)) {
						setFilters(f => ({ ...f, priority: newFilters.priority }))
					}
				}}
			/>

			<ButtonRightWrapper>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'flex-end'
					}}
				>
					{validationError && !isReadOnly && (
						<AkaTypography
							variant="danger"
							style={{ marginBottom: '8px', color: '#d93025' }}
						>
							{t(
								'approval.report.requirementsTable.validation.mustSelectAtLeastOne'
							)}
						</AkaTypography>
					)}

					{!isReadOnly && (
						<AkaButton
							key="submit"
							type="primary"
							loading={isSubmitting}
							disabled={isSubmitting || validationError}
							onClick={handleSubmit}
						>
							{t('approval.report.requirementsTable.submitButton')}
						</AkaButton>
					)}

					{isSubmitted && (
						<AkaTypography variant="tiny">
							{t('approval.report.reportAlreadySubmitted')}
						</AkaTypography>
					)}
				</div>
			</ButtonRightWrapper>
		</>
	)
}

NonContractRequirementMatrix.propTypes = {
	chapters: PropTypes.array,
	changes: PropTypes.shape({
		changedRequirementIds: PropTypes.arrayOf(PropTypes.string),
		newRequirementIds: PropTypes.arrayOf(PropTypes.string),
		changedAssignmentRequirementIds: PropTypes.arrayOf(PropTypes.string)
	}),
	preselectedRequirementIds: PropTypes.array,
	onSubmit: PropTypes.func,
	isSubmitting: PropTypes.bool,
	isReadOnly: PropTypes.bool,
	releaseId: PropTypes.string,
	isSubmitted: PropTypes.bool
}

NonContractRequirementMatrix.defaultProps = {
	chapters: [],
	changes: {
		newRequirementIds: [],
		changedRequirementIds: [],
		changedAssignmentRequirementIds: []
	},
	preselectedRequirementIds: [],
	onSubmit: () => {},
	isSubmitting: false,
	isReadOnly: false,
	isSubmitted: false
}

export default memo(NonContractRequirementMatrix)
